<template>
  <section v-if="this.hasTrackingId">
    <v-progress-linear
      v-if="this.requestState === 'LOADING'"
      :aria-label="$t('overview.headerLoading')"
      color="primary"
      indeterminate
    ></v-progress-linear>
    <RealTimeTrackingInProgress v-if="this.isJobInProgress" />
    <RealTimeTrackingFinished v-if="this.isJobFinished" />
  </section>
  <section v-else>
    <h2
      v-if="this.requestState === 'LOADING'"
      style="font-weight: 300; padding: 0 0 30px"
    >
      {{ $t('overview.headerLoading') }}
    </h2>
    <v-progress-linear
      v-if="this.requestState === 'LOADING'"
      :aria-label="$t('overview.headerLoading')"
      color="primary"
      indeterminate
    ></v-progress-linear>
  </section>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import {
  ERROR_ID_PARCEL_POSTCODE_RATE_LIMIT_REACHED,
  ERROR_ID_POSTCODE_VERIFICATION_FAILED,
  ERROR_ID_UNKNOWN_TRACKING_ID,
  TRACKING_DELIVERY_STATE_CLOSE,
  TRACKING_DELIVERY_STATE_FAR,
  TRACKING_DELIVERY_STATE_LAST,
  TRACKING_DELIVERY_STATE_NEAR,
  TRACKING_DELIVERY_STATE_UNCLEAR,
  TRACKING_STATE_BLOCKED,
  TRACKING_STATE_DELIVERED,
  TRACKING_STATE_NOT_DELIVERED,
  TRACKING_STATE_NOT_PICKED_UP,
  TRACKING_STATE_PICKED_UP,
  TRACKING_STATE_SHADOWED,
  TRACKING_STATE_UNKNOWN
} from '@/stores/constants.js';
import RealTimeTrackingInProgress from './RealTimeTrackingInProgress';
import RealTimeTrackingFinished from './RealTimeTrackingFinished';
import loadConfigByThemeId from '@/config/themes';
import { configGetter } from '@/config/configGetter';
import { getUtmSource } from '@/router/queryParam.js';
import { useGdprStore } from '@/stores/gdpr.js';
import { useThemeStore } from '@/stores/theme.js';
import { useTrackingDataStore } from '@/stores/trackingData.js';
import { useWebsocketStore } from '@/stores/websocket.js';
import { useDeliveryPeriodStore } from '@/stores/deliveryPeriod.js';
import { inputToTenant } from '@/config/countriesAndLocales.js';
import { sendAnalytics } from '@/services/analytics.js';

export default {
  name: 'RealTimeTrackingContainer',
  components: {
    RealTimeTrackingInProgress,
    RealTimeTrackingFinished
  },
  props: {
    postcode: {
      type: String,
      required: false
    },
    trackingId: {
      type: String,
      required: true
    },
    countryOrTenantId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      TRACKING_DELIVERY_STATE_CLOSE,
      TRACKING_DELIVERY_STATE_FAR,
      TRACKING_DELIVERY_STATE_LAST,
      TRACKING_DELIVERY_STATE_NEAR,
      TRACKING_DELIVERY_STATE_UNCLEAR,
      TRACKING_STATE_BLOCKED,
      TRACKING_STATE_DELIVERED,
      TRACKING_STATE_NOT_DELIVERED,
      TRACKING_STATE_NOT_PICKED_UP,
      TRACKING_STATE_PICKED_UP,
      TRACKING_STATE_SHADOWED,
      TRACKING_STATE_UNKNOWN
    };
  },
  computed: {
    ...mapState(useGdprStore, ['hasGdprConsent']),
    ...mapState(useThemeStore, ['themeId']),
    ...mapState(useTrackingDataStore, {
      trackingData: 'data',
      getTrackingState: 'getTrackingState',
      requestState: 'requestState',
      errorPayload: 'errorPayload',
      consigneeAddress: 'consigneeAddress'
    }),
    themeConfig() {
      return loadConfigByThemeId(this.themeId);
    },
    isJobInProgress() {
      return [
        TRACKING_DELIVERY_STATE_CLOSE,
        TRACKING_DELIVERY_STATE_FAR,
        TRACKING_DELIVERY_STATE_LAST,
        TRACKING_DELIVERY_STATE_NEAR,
        TRACKING_DELIVERY_STATE_UNCLEAR,
        TRACKING_STATE_BLOCKED,
        TRACKING_STATE_NOT_DELIVERED,
        TRACKING_STATE_NOT_PICKED_UP,
        TRACKING_STATE_SHADOWED,
        TRACKING_STATE_UNKNOWN
      ].includes(this.getTrackingState);
    },
    isJobFinished() {
      return [TRACKING_STATE_DELIVERED, TRACKING_STATE_PICKED_UP].includes(
        this.getTrackingState
      );
    },
    gdprConsent() {
      return (
        this.hasGdprConsent(this.$rttI18n.tenantId) ||
        configGetter(this.$rttI18n.tenantId, this.themeId).gdprApproved
      );
    },
    hasTrackingId() {
      return Object.hasOwn(this.trackingData, 'trackingId');
    }
  },
  methods: {
    configGetter,
    ...mapActions(useGdprStore, ['setGdprConsent']),
    ...mapActions(useTrackingDataStore, [
      'reloadTrackingData',
      'updateTrackingWithTitleAndSessionUpdate',
      'resetTrackingData'
    ]),
    ...mapActions(useDeliveryPeriodStore, ['readCustomizedDeliveryPeriod']),
    ...mapActions(useTrackingDataStore, ['shouldRefreshTrackingData']),
    ...mapActions(useWebsocketStore, [
      'subscribeToUpdates',
      'unSubscribeToUpdates'
    ]),
    setRoute(newTrackingData) {
      if (
        this.consigneeAddress &&
        this.consigneeAddress.postcode &&
        !this.$route.params.postcode
      ) {
        this.$router.push({
          name: 'realTimeTracking',
          params: {
            trackingId: this.$route.params.trackingId,
            postcode: this.consigneeAddress.postcode,
            countryOrTenantId: this.$rttI18n.tenantId,
            locale: this.$rttI18n.locale
          }
        });
      }
    },
    async loadTrackingData() {
      if (this.requestState === 'LOADING') {
        return;
      }
      if (
        !this.consigneeAddress ||
        this.consigneeAddress.postcode !== this.postcode ||
        this.trackingIdHasChanged() ||
        this.countryOrTenantIdHasChanged()
      ) {
        this.unSubscribeToUpdates();
        await this.reloadTrackingData({
          utmSource: getUtmSource(this.$route),
          tenantId: this.countryOrTenantId,
          ...(this.postcode && this.gdprConsent
            ? { postcode: this.postcode }
            : {}),
          trackingId: this.trackingId
        });
      }
    },
    updateTracking(source) {
      if (this.shouldRefreshTrackingData()) {
        const analyticsName =
          this.trackingData.type === 'PRIVATE'
            ? 'tracking_page'
            : 'postcode_page';
        sendAnalytics(
          this.trackingData,
          'visibility_refreshed',
          `refresh_visibility_changed_via_${analyticsName}`,
          { source }
        );
      }

      this.updateTrackingWithTitleAndSessionUpdate(
        {
          utmSource: getUtmSource(this.$route),
          tenantId: this.$rttI18n.tenantId,
          trackingId: this.trackingId,
          postcode: this.postcode
        },
        false,
        this.$t
      );
    },
    handleVisibilityChange() {
      this.updateTracking('visibilitychange');
    },
    handleFocus() {
      this.updateTracking('focus');
    },
    trackingIdHasChanged() {
      return (
        this.trackingData.trackingId !== this.trackingId &&
        this.trackingData.parcelNumber !== this.trackingId
      );
    },
    hasNoTrackingData() {
      return !this.trackingData.trackingId && this.trackingId;
    },
    countryOrTenantIdHasChanged() {
      return (
        this.trackingData.tenantId !== inputToTenant(this.countryOrTenantId)
      );
    }
  },
  watch: {
    async postcode(newPostcode, oldPostcode) {
      if (oldPostcode && !newPostcode) {
        await this.loadTrackingData();
      }
      if (newPostcode && oldPostcode && newPostcode !== oldPostcode) {
        await this.loadTrackingData();

        if (
          this.requestState === 'ERROR' &&
          [
            ERROR_ID_POSTCODE_VERIFICATION_FAILED,
            ERROR_ID_PARCEL_POSTCODE_RATE_LIMIT_REACHED
          ].includes(this.errorPayload?.id)
        ) {
          this.resetTrackingData();
          await this.$router.replace({
            name: 'realTimeTracking',
            params: {
              countryOrTenantId: this.$rttI18n.tenantId,
              locale: this.$rttI18n.locale,
              trackingId: this.trackingId
            }
          });
          await this.loadTrackingData();
        }
      }
    },
    async trackingId(newTrackingId, oldTrackingId) {
      if (newTrackingId && newTrackingId !== oldTrackingId) {
        await this.loadTrackingData();
        this.readCustomizedDeliveryPeriod({ trackingId: newTrackingId });

        if (
          this.requestState === 'ERROR' &&
          this.errorPayload?.id === ERROR_ID_UNKNOWN_TRACKING_ID
        ) {
          this.resetTrackingData();
          await this.$router.replace({
            name: 'localeLandingContainer',
            params: {
              countryOrTenantId: this.$rttI18n.tenantId,
              locale: this.$rttI18n.locale
            }
          });
        }
      }
    },
    async countryOrTenantId(newCountryOrTenantId, oldCountryOrTenantId) {
      if (
        newCountryOrTenantId &&
        newCountryOrTenantId !== oldCountryOrTenantId
      ) {
        await this.loadTrackingData();

        if (
          this.requestState === 'ERROR' &&
          this.errorPayload?.id === ERROR_ID_UNKNOWN_TRACKING_ID
        ) {
          this.resetTrackingData();
          await this.$router.replace({
            name: 'localeLandingContainer',
            params: {
              countryOrTenantId: this.countryOrTenantId,
              locale: this.$rttI18n.locale
            }
          });
        }
      }
    }
  },
  mounted() {
    if (this.hasNoTrackingData()) {
      this.loadTrackingData();
    }

    const trackingDataStore = useTrackingDataStore();
    trackingDataStore.$subscribe((mutation, state) => {
      if (state.requestState === 'SUCCESS') {
        this.setRoute(state.data);
      }
    });
  },
  created() {
    this.readCustomizedDeliveryPeriod({ trackingId: this.trackingId });
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('focus', this.handleFocus);
  },
  unmounted() {
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    window.removeEventListener('focus', this.handleFocus);
  }
};
</script>

<style scoped></style>
